import { Component, type ErrorInfo, type ReactNode } from "react";

import { LazyLoadError } from "./components";
import type { LazyErrorBoundaryProps } from "./types";

interface State {
  hasError: boolean;
  error?: Error;
  retryCount: number;
}

/**
 * @description Error boundary for lazy loaded components
 */
export class LazyErrorBoundary extends Component<LazyErrorBoundaryProps, State> {
  private maxRetries = 3;

  constructor(props: LazyErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Note: Sentry error reporting has been removed
    console.error("Lazy load error:", error);

    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Lazy load error:", error, errorInfo);

    // Note: Sentry error reporting has been removed

    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = (): void => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState({
        hasError: false,
        error: undefined,
        retryCount: this.state.retryCount + 1,
      });
    }
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Check if this is a RichTextEditor error (Slate.js node path error)
      const errorMessage = this.state.error.message || "";
      const isRichTextEditorError =
        errorMessage.includes("Cannot find a descendant at path") ||
        errorMessage.includes("wangEditor") ||
        errorMessage.includes("slate");

      // For RichTextEditor errors, try to recover more aggressively
      if (isRichTextEditorError) {
        if (this.state.retryCount < this.maxRetries) {
          // Auto-retry RichTextEditor errors after a short delay
          setTimeout(() => {
            this.handleRetry();
          }, 1000);

          // Show a loading state while retrying
          return (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                backgroundColor: "#f5f5f5",
                borderRadius: "6px",
              }}
            >
              <div style={{ marginBottom: "8px" }}>Recovering editor...</div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                Attempt {this.state.retryCount + 1} of {this.maxRetries}
              </div>
            </div>
          );
        } else {
          // If max retries exceeded for RichTextEditor, show minimal fallback
          console.warn("Max retries exceeded for RichTextEditor, showing minimal fallback");
          return (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                backgroundColor: "#fff2f0",
                border: "1px solid #ffccc7",
                borderRadius: "6px",
                color: "#cf1322",
              }}
            >
              <div style={{ marginBottom: "8px", fontWeight: 500 }}>
                Editor Temporarily Unavailable
              </div>
              <div style={{ fontSize: "12px" }}>Please refresh the page to try again</div>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  marginTop: "12px",
                  padding: "4px 8px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                }}
              >
                Refresh Page
              </button>
            </div>
          );
        }
      }

      // For other errors, use the regular fallback logic
      if (this.state.retryCount >= this.maxRetries) {
        console.warn("Max retries exceeded for lazy component, rendering empty fallback");
        return null;
      }

      const FallbackComponent = this.props.fallback || LazyLoadError;
      return <FallbackComponent error={this.state.error} retry={this.handleRetry} />;
    }

    return this.props.children;
  }
}
